import {
  type ReactNode,
  isValidElement,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';

let nextTitleId = 0;
const titleStack = new Map<number, string>();

const initialTitle =
  typeof document === 'undefined' ? 'RealAdvisor CRM' : document.title;

const topmostId = (): number | null => {
  let topId: number | null = null;
  for (const id of titleStack.keys()) {
    if (topId == null || id > topId) {
      topId = id;
    }
  }
  return topId;
};

const updateDocumentTitle = () => {
  const topId = topmostId();
  const latestTitle =
    (topId != null ? titleStack.get(topId) : null) || initialTitle;
  document.title = latestTitle;
};

const renderTitleText = (node: ReactNode): string => {
  if (node == null || typeof node === 'boolean') {
    return '';
  }

  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node.map(renderTitleText).join('');
  }

  if (isValidElement<{ children?: ReactNode }>(node)) {
    return renderTitleText(node.props.children);
  }

  return '';
};

export const useDocumentTitle = (children: ReactNode): void => {
  const idRef = useRef<number | null>(null);
  if (idRef.current == null) {
    idRef.current = nextTitleId;
    nextTitleId += 1;
  }
  const id = idRef.current;
  const title = useMemo(() => renderTitleText(children), [children]);

  useLayoutEffect(() => {
    titleStack.set(id, '');

    return () => {
      titleStack.delete(id);
      updateDocumentTitle();
    };
  }, [id]);

  useLayoutEffect(() => {
    titleStack.set(id, title);
    updateDocumentTitle();
  }, [id, title]);
};

export const DocumentTitle = ({ children }: { children: ReactNode }) => {
  useDocumentTitle(children);
  return null;
};
